import React from 'react'
import { fetchCandidates, fetchScoreRun } from 'api/candidates'
import type { components } from 'generated/api'
import { useModal, useLanguage } from 'hooks'
import { personalityOverviewTab } from 'components/modals/TestsReportModal/model'
import { TAB_NAMES } from './model'

type TalentScores = components['schemas']['ServedTalentResult']

type TalentRow = {
  area: string
  personalityScore: number
  interestScore: number
  matchScore: number
  rawCategory: string
  description: string
  category: string
  code: string
  value: number
}

const useModalQueries = () => {
  const language = useLanguage()

  const handleFetchCandidates = React.useCallback(
    async ({ id }) => await fetchCandidates({ id, query: { lang: language } }),
    [language],
  )

  return {
    handleFetchCandidates,
  }
}

export const useJobSeekerResultsModalState = state => {
  const { isOpen, jobSeeker } = state
  const { handleFetchCandidates } = useModalQueries()
  const language = useLanguage()
  const { isOpen: isGmaModalOpen, handleClose, handleOpen } = useModal()
  const [gmaScore, setGmaScore] = React.useState(null)
  const [showGma, setShowGma] = React.useState(false)
  const [showGmaToggle, setShowGmaToggle] = React.useState(false)
  const [talentData, setTalentData] = React.useState<TalentRow[]>([])
  const [candidateResponse, setCandidateResponse] = React.useState<{
    talent_scores?: TalentScores
    [key: string]: unknown
  } | null>(null)
  // Every algorithm version that has scored this candidate, newest first. More than one
  // means an older result was recalculated rather than replaced.
  const [scoreRuns, setScoreRuns] = React.useState<
    { id: string; is_primary?: boolean }[]
  >([])
  // null = show the primary (current algorithm). Otherwise the id of an older run.
  const [selectedRunId, setSelectedRunId] = React.useState<string | null>(null)
  // Older payloads are fetched on demand and kept, so switching tabs back is instant.
  const [runPayloads, setRunPayloads] = React.useState<
    Record<string, TalentScores>
  >({})
  const [activeTab, setActiveTab] = React.useState(TAB_NAMES.OVERVIEW)
  const [personalityTab, setPersonalityTab] = React.useState(
    personalityOverviewTab().id,
  )
  const [selectedCandidates, setSelectedCandidates] = React.useState({})

  const handleTab = React.useCallback(tabId => {
    setActiveTab(tabId)
    setPersonalityTab(personalityOverviewTab().id)
  }, [])

  React.useEffect(() => {
    if (!jobSeeker?.id) {
      return
    }
    setSelectedCandidates({ [jobSeeker.id]: true })
  }, [jobSeeker?.id])

  React.useEffect(() => {
    if (!isOpen || !jobSeeker?.id) {
      return
    }
    setActiveTab(TAB_NAMES.OVERVIEW)
    setPersonalityTab(personalityOverviewTab().id)
  }, [isOpen, jobSeeker?.id])

  React.useEffect(() => {
    if (!isOpen) {
      setCandidateResponse(null)
    }
  }, [isOpen])

  React.useEffect(() => {
    if (!isOpen || !jobSeeker?.id) {
      return
    }
    let cancelled = false
    handleFetchCandidates({ id: jobSeeker.id }).then(result => {
      if (cancelled || !result?.response) {
        return
      }
      const response = result.response
      setCandidateResponse(response)
      setScoreRuns(response.score_runs || [])
      setSelectedRunId(null)
      setRunPayloads({})
      setShowGmaToggle(!!response.talent_scores?.interest_integration_with_gma)
    })
    return () => {
      cancelled = true
    }
  }, [isOpen, jobSeeker?.id, handleFetchCandidates])

  React.useEffect(() => {
    if (!selectedRunId || !jobSeeker?.id || runPayloads[selectedRunId]) {
      return
    }
    let cancelled = false
    fetchScoreRun({
      id: jobSeeker.id,
      runId: selectedRunId,
      query: { lang: language },
    }).then(result => {
      if (cancelled || !result?.response?.talent_scores) {
        return
      }
      setRunPayloads(current => ({
        ...current,
        [selectedRunId]: result.response.talent_scores,
      }))
    })
    return () => {
      cancelled = true
    }
  }, [selectedRunId, jobSeeker?.id, language, runPayloads])

  // Which result the whole modal renders: the primary, or the older run being viewed.
  const activeTalentScores = React.useMemo(() => {
    if (selectedRunId) {
      return runPayloads[selectedRunId] || null
    }
    return candidateResponse?.talent_scores || null
  }, [selectedRunId, runPayloads, candidateResponse])

  React.useEffect(() => {
    if (!activeTalentScores) {
      setTalentData([])
      return
    }
    const dataKey = showGma
      ? 'interest_integration_with_gma'
      : 'interest_integration'
    const talents = activeTalentScores[dataKey]
    if (!talents) {
      // An older result may predate GMA blending entirely; fall back rather than blank out.
      const fallback = activeTalentScores.interest_integration
      if (!fallback) {
        setTalentData([])
        return
      }
      setTalentData(
        fallback.map(talent => ({
          area: talent.name,
          personalityScore: Math.round(talent.percentile * 100),
          interestScore: Math.round(talent.interest_rescaled),
          matchScore: Math.round(talent.match),
          rawCategory: talent.category,
          description: talent.description,
          category: talent.category,
          code: talent.code,
          value: talent.match,
        })),
      )
      return
    }

    setTalentData(
      talents.map(talent => ({
        area: talent.name,
        personalityScore: Math.round(talent.percentile * 100),
        interestScore: Math.round(talent.interest_rescaled),
        matchScore: Math.round(talent.match),
        rawCategory: talent.category,
        description: talent.description,
        category: talent.category,
        code: talent.code,
        value: talent.match,
      })),
    )
  }, [activeTalentScores, showGma])

  const handleConfirm = () => {
    handleClose()
  }

  return {
    state: {
      talentData,
      isGmaOpen: isGmaModalOpen,
      gmaScore,
      showGma,
      showGmaToggle,
      language,
      candidateResponse,
      activeTab,
      personalityTab,
      selectedCandidates,
      scoreRuns,
      selectedRunId,
      // An older run whose payload has not arrived yet, so the panel can say so rather
      // than render an empty table.
      isLoadingRun: !!selectedRunId && !runPayloads[selectedRunId],
    },
    handlers: {
      handleOpen,
      handleClose,
      handleConfirm,
      setGmaScore,
      setShowGma,
      handleTab,
      setPersonalityTab,
      setSelectedCandidates,
      setSelectedRunId,
    },
  }
}
